// QV-7124 — the TIMING half of useDeziDiscounts (debounce → dedup by request key → settle), kept free
// of React so it can be unit-tested with fake timers.
//
// It owns at most ONE pending lookup — debounced or in flight — plus the key of the last lookup that
// settled. The rules that matter for money:
//
//  • An unchanged key NEVER cancels the pending lookup. React re-runs the hook's effect whenever the
//    `lines` array reference changes (e.g. setLineTaxes) even though the basket is the same; cancelling
//    there and then deduping the re-run away dropped the discount entirely.
//  • A changed key cancels the pending lookup (its result is ignored) and schedules the new one —
//    unless the new key is the one that already settled, whose result is still what the slice holds.
//  • awaitSettled follows whatever is pending for the CURRENT basket, including a lookup that is
//    still inside its debounce window and one that replaced a cancelled lookup mid-wait.

const TIMEOUT = Symbol("dezi-wait-timeout");

/**
 * @param {Object} o
 * @param {number} o.debounceMs
 */
export function createDeziLookupScheduler({ debounceMs }) {
  let pending = null; // { key, cancelled, timer, done, resolveDone }
  let settledKey = null;

  const cancelPending = () => {
    if (!pending) return;
    const entry = pending;
    pending = null;
    entry.cancelled = true;
    clearTimeout(entry.timer);
    entry.resolveDone();
  };

  return {
    /**
     * Ask for `key`. `run(isCancelled)` performs the lookup and returns a promise; it must check
     * `isCancelled()` before applying its result. Returns true when a lookup was scheduled.
     */
    request(key, run) {
      if (pending && pending.key === key) return false; // already debounced / in flight for this basket
      cancelPending();
      if (key === settledKey) return false; // the slice already holds this basket's result
      const entry = { key, cancelled: false, timer: null, done: null, resolveDone: null };
      entry.done = new Promise((resolve) => {
        entry.resolveDone = resolve;
      });
      entry.timer = setTimeout(() => {
        let p;
        try {
          p = Promise.resolve(run(() => entry.cancelled));
        } catch (e) {
          p = Promise.reject(e);
        }
        p.catch(() => undefined).then(() => {
          if (pending === entry) {
            pending = null;
            settledKey = key;
          }
          entry.resolveDone();
        });
      }, debounceMs);
      pending = entry;
      return true;
    },

    /** Drop the pending lookup and forget the settled key, so the next request always fetches. */
    reset() {
      cancelPending();
      settledKey = null;
    },

    /** True while a lookup is debounced or in flight. */
    hasPending() {
      return pending !== null;
    },

    /** Key of the pending lookup, or null. */
    pendingKey() {
      return pending ? pending.key : null;
    },

    /**
     * Resolve true once nothing is pending (following replacements), false after `timeoutMs`.
     */
    async awaitSettled(timeoutMs) {
      let timer;
      const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve(TIMEOUT), timeoutMs);
      });
      try {
        for (;;) {
          const entry = pending;
          if (!entry) return true;
          // eslint-disable-next-line no-await-in-loop
          const winner = await Promise.race([entry.done, timeout]);
          if (winner === TIMEOUT) return false;
          // Settled or superseded — loop to pick up a newer lookup for the current basket.
        }
      } finally {
        clearTimeout(timer);
      }
    },
  };
}
